import { PayFor } from './GrooverTypes'
import { GroupRefundAllocation, PayForGroupSettlement } from './QueryParamTypes'

export enum SwishPaymentStatus {
  PENDING = 'PENDING',
  CREATED = 'CREATED',
  DECLINED = 'DECLINED', // declined by user
  CANCELLED = 'CANCELLED', // cancelled by us
  PAID = 'PAID',
  ERROR = 'ERROR'
}

export enum PaymentReferenceType {
  ENROLLMENT = 'enrollment',
  PAYMENT_TRANSACTION = 'payment_transaction',
  PRIVATE_LESSON_ORDER = 'private_lesson_order',
  WalkIn = 'walk_in',
  /** V4 grouped "Pay for both" — reference is `grp-{enrollmentGroupId}`. */
  GROUP = 'group'
}

export type PaymentReferencePrefixType = Record<PaymentReferenceType, string>

export const PaymentReferencePrefix = {
  [PaymentReferenceType.ENROLLMENT]: '',
  [PaymentReferenceType.PAYMENT_TRANSACTION]: 'pt-',
  [PaymentReferenceType.PRIVATE_LESSON_ORDER]: 'plo-',
  [PaymentReferenceType.WalkIn]: 'walkin-',
  [PaymentReferenceType.GROUP]: 'grp-'
}

export type SwishPaymentOKResponse = {
  instructionUUID: string // this is the same as response.headers.location.split('/').pop()
  // response.headers.paymentrequesttoken received when creating payment in Swish
  // needed for m-commerce flow, see https://developer.swish.nu/documentation/guides/create-a-payment-request#m-commerce
  token?: string
}

export type SwishResponseError = {
  isError: boolean
  source: string
  message: string
  errorCode?: string // Swish error code, e.g. 'ACMT01'
  /**
   * True when the request never reached a verdict — our own HTTP timeout fired, or the socket
   * failed. Unlike a Swish rejection (which always carries a response body and an errorCode),
   * the request may well have been accepted, so callers must not assume it does not exist.
   */
  isTransportFailure?: boolean
}

export type CreatePaymentInput = {
  enrollmentId: string
  payFor: PayFor
  /** Optional custom amount in öre (cents). When provided, must be 1-remaining. Otherwise uses full remaining. */
  amountInCents?: number
} & (
  | { usePhoneFromEnrollment: true; payerPhone?: never } // Use phone from enrollment
  | { usePhoneFromEnrollment?: false; payerPhone: string } // Use provided phone
)

export type CreatePaymentForPaymentTransactionInput = {
  paymentTransactionId: string
  payFor: PayFor
  // https://developer.swish.nu/documentation/guides/create-a-payment-request#create-payment-request
  flowType: 'ecommerce' | 'mcommerce'
  // make phoneNumber required if flowType is ecommerce
} & ({ flowType: 'ecommerce'; phoneNumber: string } | { flowType: 'mcommerce' })

export type CreatePaymentForPrivateLessonOrderInput = {
  clientId: string
  orderId: string
  payerPhone: string // the phone number of the person paying (format: 46706112233)
}

export type CancelPaymentInput = {
  instructionUUID: string
}

// see https://developer.swish.nu/api/payment-request/v2
export type CreatePaymentRequest = {
  payeePaymentReference: string // our payment ID (enrollment ID)
  callbackUrl: string // webhook endpoing that should be called after payment is confirmed by user
  // payeeAlias = The Swish number of the payee. It needs to match with Merchant Swish number
  // in prod it must match certificate's common name
  payeeAlias: string // Swish phone number, like 12300112233
  // payerAlias is needed only for e-commerce flow (web); for m-commerce it should be NOT SET
  // see https://developer.swish.nu/documentation/guides/create-a-payment-request#create-payment-request
  payerAlias?: string // the phone number of the person paying (format: 46706112233)
  amount: string // must be a string, in SEK, with a dot as decimal separator; 2 decimals max
  currency: string // usually 'SEK'
  message: string // message about the payment/order
  callbackIdentifier?: string // optional, a unique identifier for the callbac
}

export type SwishPaymentIntentStatus = {
  swishEnvironment: SwishConfigEnvironment
  swishOperator: string
  instructionUUID: string // generared by us when creating payment and used by Swish to identify the request
  status: string // 'CREATED' | 'PAID' | 'REFUND' ...
  payeePaymentReference: string // our enrollment ID; set only when creating payment intent
  paymentReference?: string // our enrollment ID
  swishPaymentReference?: string // generated by Swish when creating payment and returned in payment object
  payerAlias?: string // phone number of the payer (optional — absent in M-Commerce/QR walk-in flow)
  currency: string
  amount: number
  userId?: string
  type: 'payment' | 'refund' // indicates whether this is a payment or refund intent
  originalPaymentReference?: string // for refunds only: reference to the original payment for refunds; this is NOT enrollment ID and NOT instructionUUID (id) of original swish payment; this is generated by Swish when creating payment and returned in payment object
  errorCode?: string // Swish error code when status is ERROR/DECLINED (e.g. RF07, BANKIDCL, FF10, TM01, DS24)
  errorMessage?: string // Human-readable error message from Swish
  /** Set when this intent settles a group "Pay for both" payment; unset for single-enrollment payments. */
  groupSettlement?: PayForGroupSettlement
  /** Refunds of a group payment: book on the payer's enrollment only, or split across both. */
  groupRefundAllocation?: GroupRefundAllocation
}

export type SwishRefundPaidWebhookResponse = {
  id: string // instruction UUID
  amount: string // amount with 2 decimal places
  originalPaymentReference: string // reference to the original payment
  datePaid: string // ISO date string
  payerPaymentReference: string // enrollment ID
  paymentReference: string // Swish refund ID
  message: string
  dateCreated: string // ISO date string
  payerAlias: string // payer's Swish number
  callbackUrl: string
  currency: string // 'SEK'
  payeeAlias: string // payee's phone number
  status: 'PAID'
}

export type SwishPaymentIntentStatusUpdate = Partial<SwishPaymentIntentStatus> & {
  instructionUUID: string
}

export type SwishPaymentIntentWebhookResponse = {
  id: string // '89DD769446C37EAD4CEBA07CFB787406' // instruction UUID; generared by us when creating payment and used by Swish to identify the request
  payeePaymentReference: string // 'enrollment-id-0123456789' // our enrollment ID
  paymentReference?: string // '5BA9A7725FF7418A9D6CC99A24EBB7D2' // Swish payment ID, received only in PUT or GET /v1/paymentrequests/{id} response
  callbackUrl: string // 'https://25df-83-10-153-190.ngrok-free.app'
  callbackIdentifier: string // 'D0194DE14DFC448D9A2F701567547600' // not used; we could set it to our ID when creating payment in Swish
  payerAlias: string // '4671234768' // phone number of the payer
  payeeAlias: string // '1231181189' // our Swish number
  currency: string // 'SEK'
  message: string // 'Kingston USB Flash Drive 8 GB'
  errorMessage?: string
  status: string // 'PAID'
  amount: number //
  dateCreated: string // '2024-12-15T18:03:50.727+0100'
  datePaid: string // '2024-12-15T18:03:54.728+0100'
  errorCode?: string
}

export type SwishHttpsAgentProd = {
  cert: string // .pem file from Swish
  key: string // our private key
  passphrase: string // pass for our private key
}

export type SwishHttpsAgentTest = {
  ca: string // usually Swish_TLS_RootCA.pem file
  // .p12 file; its type of Buffer, but we need to store it as Base64 string when saving into Firestore (see upsertSwishConfig)
  // Base64 is decoded back to Buffer when reading data from Firestore (see getSwishConfig)
  pfx: Buffer | string
  passphrase: string // password for .p12 file; for test env it's "swish"
}

export enum SwishConfigEnvironment {
  Test = 'test',
  Production = 'production'
}

type SwishConfigBase = {
  endpoint: string
  payeeAlias: string // phone number who receives money (format: 1234111308)
  callbackUrl: string // webhook callback to be called after create swish payment
}

type SwishConfigProd = SwishConfigBase & {
  environment: SwishConfigEnvironment.Production
  httpsAgent: SwishHttpsAgentProd
}

type SwishConfigTest = SwishConfigBase & {
  environment: SwishConfigEnvironment.Test
  httpsAgent: SwishHttpsAgentTest
}

export type SwishConfig = SwishConfigProd | SwishConfigTest

export type SwishConfigOperator = {
  production: SwishConfigProd
  test: SwishConfigTest
}

// https://developer.swish.nu/api/refunds/v2#create-refund
export type CreateRefundRequest = {
  originalPaymentReference: string // The payment reference of the original payment (payment.paymentReference)
  payerAlias: string // The Swish number of the payer
  payeeAlias: string // The Swish number of the payee
  amount: string // The amount to refund
  currency: string // The currency of the refund
  message: string // The message to show to the payer
  payerPaymentReference: string // The payment reference of the original payment (our enrollment ID)
  callbackUrl: string // The URL that Swish will use to notify about the refund result
}

export type CreateRefundInput = {
  instructionUUID: string // The original payment's instruction UUID
  amountInCents: number // The amount to refund in cents
  message: string // The message to show to the payer
  /** Required by admin-web when refunding a V4 "Pay for both" group payment. */
  groupRefundAllocation?: GroupRefundAllocation
}

export type RefundableSwishPayment = {
  instructionUUID: string // The original payment's instruction UUID
  amount: number // The amount that can be refunded (in cents)
  originalAmount: number // The original payment amount (in cents)
  alreadyRefunded: number // Amount already refunded (in cents)
}

export type RefundableVippsPayment = {
  reference: string // Vipps payment reference
  amount: number // The amount that can be refunded (in cents)
  originalAmount: number // The original payment amount (in cents)
  alreadyRefunded: number // Amount already refunded (in cents)
}
